import { apiService } from './api';
import { FEATURES } from '../config';
import { authService } from './authService';
import {
  isWebPushSupported,
  urlBase64ToUint8Array,
  type WebPushEnableResult,
} from '../utils/webPush';

export const PUSH_PREFERENCE_CHANGED_EVENT = 'app:pushPreferenceChanged';
export const PUSH_OPT_IN_DISMISSED_KEY = 'pwa-push-opt-in-dismissed';

interface WebPushPublicKeyDto {
  enabled?: boolean;
  Enabled?: boolean;
  publicKey?: string | null;
  PublicKey?: string | null;
}

interface PushSubscriptionJson {
  endpoint: string;
  keys?: {
    p256dh?: string;
    auth?: string;
  };
}

let publicKeyCache: string | null | undefined;

async function getVapidPublicKey(): Promise<string | null> {
  if (typeof publicKeyCache === 'string') return publicKeyCache;
  try {
    const data = await apiService.get<WebPushPublicKeyDto>('/api/Push/vapid-public-key', { cache: false });
    const enabled = Boolean(data?.enabled ?? data?.Enabled);
    const key = (data?.publicKey ?? data?.PublicKey ?? '').trim();
    publicKeyCache = enabled && key ? key : null;
    return publicKeyCache;
  } catch (error) {
    console.debug('Web Push public key unavailable:', error);
    return null;
  }
}

async function getPushManager(): Promise<PushManager | null> {
  if (!isWebPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager ?? null;
}

function subscriptionJson(subscription: PushSubscription): PushSubscriptionJson | null {
  const json = subscription.toJSON() as PushSubscriptionJson;
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
  return json;
}

async function saveSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscriptionJson(subscription);
  if (!json) return;
  await apiService.post('/api/Push/subscribe', {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
}

/**
 * Call from a user-gesture handler (settings save / Allow button) so iOS can prompt.
 */
export async function enableWebPushFromUserGesture(): Promise<WebPushEnableResult> {
  if (!FEATURES.enablePushNotifications || !isWebPushSupported()) {
    return 'unsupported';
  }

  const publicKey = await getVapidPublicKey();
  if (!publicKey) return 'unsupported';

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'skipped';
  }

  const pushManager = await getPushManager();
  if (!pushManager) return 'unsupported';

  let subscription = await pushManager.getSubscription();
  if (!subscription) {
    subscription = await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  await saveSubscription(subscription);
  await persistPushNotificationsPreference(true);
  return 'subscribed';
}

export async function persistPushNotificationsPreference(enabled: boolean): Promise<void> {
  try {
    const prefs = await authService.getLearningPreferences();
    if (prefs.pushNotifications !== enabled) {
      await authService.updateLearningPreferences({ ...prefs, pushNotifications: enabled });
    }
    if (enabled) {
      localStorage.removeItem(PUSH_OPT_IN_DISMISSED_KEY);
    }
    window.dispatchEvent(new CustomEvent(PUSH_PREFERENCE_CHANGED_EVENT, { detail: { enabled } }));
  } catch (error) {
    console.debug('Web Push preference save skipped:', error);
  }
}

/** Refresh an existing granted subscription after login (no permission prompt). */
export async function syncWebPushSubscriptionIfGranted(): Promise<void> {
  if (!FEATURES.enablePushNotifications || !isWebPushSupported()) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  const publicKey = await getVapidPublicKey();
  if (!publicKey) return;

  const pushManager = await getPushManager();
  if (!pushManager) return;

  let subscription = await pushManager.getSubscription();
  if (!subscription) {
    subscription = await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  await saveSubscription(subscription);
}

export async function disableWebPushOnThisDevice(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    try {
      await apiService.post('/api/Push/unsubscribe', { endpoint: subscription.endpoint });
    } catch {
      // Still drop the local subscription so this device stops receiving.
    }
    await subscription.unsubscribe();
  } catch (error) {
    console.debug('Web Push unsubscribe skipped:', error);
  }
}
